import { Check, Coins, Globe, Landmark, LayoutGrid, Sigma, WalletCards } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { type ComponentType, type ReactNode, type SVGProps, useRef } from 'react'

import { OverlayThumbY } from '@/components/widget/overlay-scroll-y'
import type { MarketType } from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard'
import {
  FilterMenu,
  type FilterOption,
  FilterRow,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-filter-controls'
import type {
  WizardView,
  WizardViewId,
} from '@/features/dashboard/components/watchlist-wizard/watchlist-wizard-views'
import { cn } from '@/lib/utils'
import type { ChartCompareMode } from '@/state/chart-compare'
import type { Exchange } from '@/types/generated/common/types'

// Hyperliquid's swirl alone, in currentColor — the brand logo's mint-on-teal
// disc stood out as the one colored glyph among the monochrome lucide icons.
const HyperliquidMark = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="2 2 12 12" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M13.9996 7.95505C14.0105 8.98408 13.8044 9.96735 13.3994 10.9068C12.821 12.2445 11.4345 13.3384 10.1684 12.174C9.13581 11.225 8.94424 9.29849 7.39719 9.01647C5.35023 8.7573 5.30098 11.2365 3.96371 11.5166C2.4732 11.833 1.9788 9.21464 2.00069 8.02555C2.02258 6.83645 2.32543 5.16523 3.62073 5.16523C5.11124 5.16523 5.21158 7.52246 7.10346 7.39479C8.97709 7.2614 9.00992 4.80888 10.2341 3.7589C11.2904 2.85183 12.5328 3.51688 13.1549 4.60879C13.7314 5.61877 13.985 6.80405 13.9977 7.95505H13.9996Z" />
  </svg>
)

const VIEW_ICONS: Record<WizardViewId, ComponentType<SVGProps<SVGSVGElement>>> = {
  all: LayoutGrid,
  hyperliquid: HyperliquidMark,
  cme: Landmark,
  tokenized: Coins,
  options: Sigma,
}

const COMPARE_MODE_KEYS: Record<ChartCompareMode, string> = {
  percent: 'percent',
  'new-scale': 'newScale',
  'new-pane': 'newPane',
  ghost: 'ghost',
  'ghost-spread': 'ghostSpread',
}

function SidePanelSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-foreground/10 flex flex-col gap-1 border-b px-2 py-3 last:border-b-0">
      <h3 className="text-foreground/40 px-2 pb-1 text-[10px] font-semibold tracking-wider uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

function SidePanelOption({
  icon,
  label,
  selected,
  onClick,
}: {
  icon?: ReactNode
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-xs transition-colors',
        selected
          ? 'bg-emphasis text-emphasis-foreground'
          : 'text-foreground/60 hover:bg-foreground/5 hover:text-foreground'
      )}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {selected ? <Check className="size-3.5 shrink-0" aria-hidden="true" /> : null}
    </button>
  )
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="text-foreground/40 px-2 pt-1.5 text-[11px]">{children}</span>
}

interface WatchlistWizardSidePanelProps {
  views: WizardView[]
  activeViewId: WizardViewId | null
  onApplyView: (view: WizardView) => void

  /** Absent when the caller locked the picker to one exchange. */
  exchangeOptions?: FilterOption<Exchange>[]
  exchangeFilters: Set<Exchange>
  exchangeSummary: string
  onToggleExchange: (exchange: Exchange) => void
  onResetExchanges: () => void

  quoteOptions: string[]
  quoteFilter: string | null
  onQuoteChange: (quote: string | null) => void

  /** Absent when the caller locked the picker to one market type. */
  typeOptions?: FilterOption<MarketType>[]
  typeFilters: Set<MarketType>
  onToggleType: (type: MarketType) => void
  onResetTypes: () => void

  /** Present only when the picker was opened from a chart. */
  compare?: {
    mode: ChartCompareMode | null
    /** The chart engine's own modes, in display order. */
    modes: ChartCompareMode[]
    allowNone: boolean
    onModeChange: (mode: ChartCompareMode | null) => void
  }
}

// Desktop-only left rail: one-click presets, the filters that don't earn
// header space, and — from a chart — how a pick lands on it. Below md the
// header's Exchange/Type menus stand in for it.
export function WatchlistWizardSidePanel({
  views,
  activeViewId,
  onApplyView,
  exchangeOptions,
  exchangeFilters,
  exchangeSummary,
  onToggleExchange,
  onResetExchanges,
  quoteOptions,
  quoteFilter,
  onQuoteChange,
  typeOptions,
  typeFilters,
  onToggleType,
  onResetTypes,
  compare,
}: WatchlistWizardSidePanelProps) {
  const t = useTranslations('watchlistWizard')
  const quoteActiveSet = new Set(quoteFilter === null ? [] : [quoteFilter])
  const scrollerRef = useRef<HTMLDivElement>(null)

  return (
    <aside
      aria-label={t('sidePanel.ariaLabel')}
      className="group/scroll-y border-foreground/10 relative hidden w-52 shrink-0 border-r md:flex"
    >
      <div
        ref={scrollerRef}
        className="scrollbar-none flex min-h-0 flex-1 flex-col overflow-y-auto"
      >
        <SidePanelSection title={t('sidePanel.savedViews')}>
          {views.map((view) => {
            const Icon = VIEW_ICONS[view.id]
            return (
              <SidePanelOption
                key={view.id}
                icon={<Icon className="size-3.5 shrink-0" aria-hidden="true" />}
                label={t(`sidePanel.views.${view.id}`)}
                selected={activeViewId === view.id}
                onClick={() => onApplyView(view)}
              />
            )
          })}
        </SidePanelSection>

        <SidePanelSection title={t('sidePanel.refine')}>
          {exchangeOptions ? (
            <>
              <FieldLabel>{t('exchange')}</FieldLabel>
              <FilterMenu
                block
                icon={<Globe className="size-3.5 shrink-0" aria-hidden="true" />}
                summary={exchangeSummary}
                options={exchangeOptions}
                activeSet={exchangeFilters}
                allLabel={t('sidePanel.anyExchange')}
                onToggle={onToggleExchange}
                onReset={onResetExchanges}
              />
            </>
          ) : null}
          {quoteOptions.length > 0 ? (
            <>
              <FieldLabel>{t('sidePanel.quote')}</FieldLabel>
              <FilterMenu
                block
                icon={<WalletCards className="size-3.5 shrink-0" aria-hidden="true" />}
                summary={quoteFilter ?? t('sidePanel.anyQuote')}
                options={quoteOptions.map((quote) => ({ value: quote, label: quote }))}
                activeSet={quoteActiveSet}
                allLabel={t('sidePanel.anyQuote')}
                onToggle={(quote) => onQuoteChange(quote === quoteFilter ? null : quote)}
                onReset={() => onQuoteChange(null)}
              />
            </>
          ) : null}
          {typeOptions ? (
            <>
              <FieldLabel>{t('sidePanel.product')}</FieldLabel>
              <div className="px-1">
                <FilterRow
                  options={typeOptions}
                  activeSet={typeFilters}
                  allLabel={t('filterAllOption')}
                  onToggle={onToggleType}
                  onReset={onResetTypes}
                  collapsed={false}
                />
              </div>
            </>
          ) : null}
        </SidePanelSection>

        {compare ? (
          <SidePanelSection title={t('sidePanel.compareOnChart')}>
            {compare.allowNone ? (
              <SidePanelOption
                label={t('sidePanel.compareModes.none')}
                selected={compare.mode === null}
                onClick={() => compare.onModeChange(null)}
              />
            ) : null}
            {compare.modes.map((mode) => (
              <SidePanelOption
                key={mode}
                label={t(`sidePanel.compareModes.${COMPARE_MODE_KEYS[mode]}`)}
                selected={compare.mode === mode}
                onClick={() => compare.onModeChange(mode)}
              />
            ))}
          </SidePanelSection>
        ) : null}
      </div>
      <OverlayThumbY scroller={scrollerRef} />
    </aside>
  )
}
